// ==========================================
// pages/Exam/Structure.jsx - FULL FIXED VERSION WITH REAL-TIME ANSWER SAVING
// ==========================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AntiCheat from '../../components/AntiCheat/AntiCheat';
import PreExamCheck from '../../components/PreExamCheck/PreExamCheck';
import { formatTime } from '../../utils/helpers';
import { 
  Container, Paper, Typography, Radio, RadioGroup, 
  FormControlLabel, Button, LinearProgress, Box, 
  CircularProgress, Divider, Alert,
  Card, CardContent, Snackbar,
  IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import axios from '../../services/axios';

const Structure = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(25 * 60);
  const [examStarted, setExamStarted] = useState(false);
  const [showPreCheck, setShowPreCheck] = useState(false);
  const [violationCount, setViolationCount] = useState(0);
  const [hasOtherTabs, setHasOtherTabs] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [backendResponse, setBackendResponse] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [showInstructions, setShowInstructions] = useState(true);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ show: false, message: '', type: 'success' });
  const [sessionId, setSessionId] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  
  const timerRef = useRef(null);
  const hasSubmittedRef = useRef(false);
  const hasFetchedRef = useRef(false);
  const questionRefs = useRef({});
  const saveTimeoutRef = useRef(null);

  const getQuestionRanges = () => {
    if (questions.length === 0) return { structure: null, written: null };
    
    const total = questions.length;
    const structure_end = Math.min(Math.floor(total * 0.4), 15);
    
    return {
      structure: { start: 1, end: structure_end },
      written: { start: structure_end + 1, end: total }
    };
  };

  const ranges = getQuestionRanges();

  // ============================================
  // SHOW SAVE STATUS SNACKBAR
  // ============================================
  const showSaveStatus = (message, type = 'success') => {
    setSaveStatus({ show: true, message, type });
    setTimeout(() => {
      setSaveStatus(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  // ============================================
  // SAVE SINGLE ANSWER TO SERVER
  // ============================================
  const saveAnswerToServer = useCallback(async (questionIndex, answer) => {
    if (!autoSaveEnabled || !sessionId) return;
    
    const questionId = questions[questionIndex]?.id;
    if (!questionId) return;

    setSaving(true);
    try {
      const response = await axios.post('/exam/save-answer', {
        session_id: sessionId,
        question_id: questionId,
        user_answer: answer,
        section: 'structure'
      });

      if (response.data.success) {
        setLastSaveTime(new Date());
        // Update question with saved status
        setQuestions(prev => {
          const newQuestions = [...prev];
          if (newQuestions[questionIndex]) {
            newQuestions[questionIndex] = {
              ...newQuestions[questionIndex],
              user_answer: answer,
              has_answered: true
            };
          }
          return newQuestions;
        });
        showSaveStatus('Answer saved successfully', 'success');
      }
    } catch (error) {
      console.error('Failed to save answer to server:', error);
      // Fallback: save to localStorage
      localStorage.setItem('structure_answers', JSON.stringify(answers));
      showSaveStatus('Saved locally (offline)', 'warning');
    } finally {
      setSaving(false);
    }
  }, [autoSaveEnabled, sessionId, questions, answers]);

  // ============================================
  // BATCH SAVE ALL ANSWERS TO SERVER
  // ============================================
  const batchSaveAnswers = useCallback(async () => {
    if (!sessionId || Object.keys(answers).length === 0) return;

    const answersArray = Object.entries(answers)
      .map(([index, answer]) => {
        const questionId = questions[parseInt(index)]?.id;
        return questionId ? { question_id: questionId, user_answer: answer } : null;
      })
      .filter(item => item !== null);

    if (answersArray.length === 0) return;

    setSaving(true);
    try {
      const response = await axios.post('/exam/batch-save-answers', {
        session_id: sessionId,
        answers: answersArray,
        section: 'structure',
        time_left: timeLeft
      });

      if (response.data.success) {
        setLastSaveTime(new Date());
        showSaveStatus(`${response.data.saved_count} answers saved`, 'success');
      }
    } catch (error) {
      console.error('Failed to batch save answers:', error);
      // Fallback: save to localStorage
      localStorage.setItem('structure_answers', JSON.stringify(answers));
      showSaveStatus('Saved locally (offline)', 'warning');
    } finally {
      setSaving(false);
    }
  }, [sessionId, answers, questions, timeLeft]);

  // ============================================
  // AUTO-SAVE EVERY 30 SECONDS
  // ============================================
  useEffect(() => {
    if (!autoSaveEnabled || !examStarted || Object.keys(answers).length === 0) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      batchSaveAnswers();
    }, 30000); // Auto-save every 30 seconds

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [answers, autoSaveEnabled, examStarted, batchSaveAnswers]);

  useEffect(() => {
    const listeningCompleted = localStorage.getItem('listening_completed');
    const listeningAnswers = localStorage.getItem('listening_answers');
    
    if (listeningCompleted !== 'true' || !listeningAnswers) {
      alert('You must complete Listening section first!');
      navigate('/exam/listening', { replace: true });
      return;
    }
    
    // Check for existing session ID
    const existingSessionId = localStorage.getItem('exam_session_id');
    if (existingSessionId) {
      setSessionId(existingSessionId);
    }
    
    const savedTime = localStorage.getItem('structure_time');
    const savedAnswers = localStorage.getItem('structure_answers');
    
    if (savedTime && parseInt(savedTime) > 0) {
      setTimeLeft(parseInt(savedTime));
    }
    
    if (savedAnswers) {
      try {
        setAnswers(JSON.parse(savedAnswers));
      } catch (e) {
        console.error('Error parsing saved answers:', e);
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    let retryCount = 0;
    const maxRetries = 3;

    const fetchQuestions = async () => {
      try {
        setLoading(true);
        setFetchError(false);
        
        const token = localStorage.getItem('token');
        const headers = token ? { 
          Authorization: `Bearer ${token}`,
          'x-session-id': sessionId 
        } : {};

        const res = await axios.get('/exam/questions/structure', { headers });
        
        setBackendResponse(res.data);

        // Set session ID from response
        const newSessionId = res.data.session_id || res.data.data?.session_id;
        if (newSessionId) {
          setSessionId(newSessionId);
          localStorage.setItem('exam_session_id', newSessionId);
          console.log('✅ Structure Session ID:', newSessionId);
        }

        let loadedQuestions = [];
        
        if (res.data && Array.isArray(res.data.questions)) {
          loadedQuestions = res.data.questions;
        } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
          loadedQuestions = res.data.data;
        } else if (res.data && Array.isArray(res.data)) {
          loadedQuestions = res.data;
        } else {
          console.error('Unexpected response format:', res.data);
          throw new Error('Invalid response format from server');
        }
        
        // Format questions with user answers if available
        const formattedQuestions = loadedQuestions.map((q, idx) => {
          const questionText = q.question_text || q.question || q.text || `Structure Question ${idx + 1}`;
          
          return {
            id: q.id || `structure_${idx + 1}`,
            session_id: q.session_id || sessionId,
            question: questionText,
            image_url: q.image_url || q.image,
            image_description: q.image_description || null,
            options: q.options || [
              { value: 'A', text: q.option_a || 'Option A' },
              { value: 'B', text: q.option_b || 'Option B' },
              { value: 'C', text: q.option_c || 'Option C' },
              { value: 'D', text: q.option_d || 'Option D' }
            ],
            user_answer: q.user_answer || null,
            has_answered: !!q.user_answer,
            correct_answer: q.correct_answer,
            raw: q
          };
        });
        
        // Update answers state from loaded questions
        const loadedAnswers = {};
        formattedQuestions.forEach((q, idx) => {
          if (q.user_answer) {
            loadedAnswers[idx] = q.user_answer;
          }
        });
        
        if (Object.keys(loadedAnswers).length > 0) {
          setAnswers(prev => ({ ...prev, ...loadedAnswers }));
        }

        setQuestions(formattedQuestions);
        setLoading(false);
        
      } catch (err) {
        console.error('Error fetching questions:', err);
        
        if (retryCount < maxRetries && err.response?.status !== 401) {
          retryCount++;
          console.log(`Retrying... (${retryCount}/${maxRetries})`);
          setTimeout(fetchQuestions, 3000);
        } else {
          setFetchError(true);
          setLoading(false);
          
          if (err.response?.status === 401) {
            alert('Session expired. Please login again.');
            navigate('/login');
          }
        }
      }
    };

    fetchQuestions();
  }, [navigate, sessionId]);

  const handlePreCheckReady = async () => {
    setShowPreCheck(false);
    const elem = document.documentElement;
    try {
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
      setExamStarted(true);
      
      // Initial save of any existing answers
      if (Object.keys(answers).length > 0 && sessionId) {
        setTimeout(() => batchSaveAnswers(), 1000);
      }
    } catch (err) {
      console.error('Fullscreen request failed:', err);
      setExamStarted(true);
    }
  };

  const handlePreCheckFail = () => {
    setShowPreCheck(false);
    navigate('/dashboard', { replace: true });
  };

  const startExam = () => {
    if (questions.length === 0) {
      alert('Cannot start exam: Questions not available. Please refresh the page or contact admin.');
      return;
    }
    setShowPreCheck(true);
  };

  // ============================================
  // MANUAL SAVE BUTTON HANDLER
  // ============================================
  const handleManualSave = async () => {
    if (Object.keys(answers).length === 0) {
      showSaveStatus('No answers to save', 'info');
      return;
    }
    
    await batchSaveAnswers();
  };

  // ============================================
  // SUBMIT SECTION HANDLER
  // ============================================
  const handleSubmit = async () => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Final save before submitting
    if (sessionId && Object.keys(answers).length > 0) {
      setSaving(true);
      try {
        await batchSaveAnswers();
        
        // Mark section as completed on server
        await axios.post(`/exam/session/${sessionId}/complete`, {
          section: 'structure'
        });
      } catch (error) {
        console.error('Error finalizing section:', error);
      } finally {
        setSaving(false);
      }
    }

    // Save to localStorage
    localStorage.setItem('structure_answers', JSON.stringify(answers));
    localStorage.setItem('structure_time', timeLeft.toString());
    localStorage.setItem('structure_completed', 'true');
    
    showSaveStatus('Section completed! Moving to Reading...', 'success');
    
    setTimeout(() => {
      navigate('/exam/reading', { replace: true });
    }, 1000);
  };

  useEffect(() => {
    window.autoSubmitExam = handleSubmit;
    return () => {
      delete window.autoSubmitExam;
    };
  }, [answers, timeLeft, sessionId]);

  useEffect(() => {
    if (!examStarted) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (window.autoSubmitExam) {
            window.autoSubmitExam();
          }
          return 0;
        }
        return prev - 1;
      });
      
      // Auto-save progress every minute
      if (timeLeft % 60 === 0 && autoSaveEnabled && sessionId) {
        batchSaveAnswers();
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [examStarted, autoSaveEnabled, sessionId, batchSaveAnswers, timeLeft]);

  // ============================================
  // HANDLE ANSWER SELECTION
  // ============================================
  const handleAnswer = async (questionIndex, value) => {
    const newAnswers = { ...answers, [questionIndex]: value };
    setAnswers(newAnswers);
    setUnansweredQuestions(prev => prev.filter(q => q !== questionIndex));

    // Auto-save to server
    if (autoSaveEnabled) {
      saveAnswerToServer(questionIndex, value);
    }
  };

  const handleFinish = () => {
    const unanswered = [];
    questions.forEach((_, index) => {
      if (!answers[index]) {
        unanswered.push(index);
      }
    });

    if (unanswered.length > 0) {
      setUnansweredQuestions(unanswered);
      const firstUnanswered = unanswered[0];
      if (questionRefs.current[firstUnanswered]) {
        questionRefs.current[firstUnanswered].scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
      
      const confirmSkip = window.confirm(
        `You have ${unanswered.length} unanswered questions. Are you sure you want to continue?`
      );
      
      if (!confirmSkip) return;
    }
    
    handleSubmit();
  };

  const handleViolation = (count) => {
    setViolationCount(count);
    if (count >= 3) {
      alert('You have violated 3 times. Exam will be auto-submitted.');
      handleSubmit();
    }
  };

  const handleTabDetection = (detected) => {
    setHasOtherTabs(detected);
  };

  const handleImageError = (questionId) => {
    setImageErrors(prev => ({ ...prev, [questionId]: true }));
  };

  const hideInstructions = () => {
    setShowInstructions(false);
  };

  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, textAlign: 'center' }}>
        <Paper elevation={8} sx={{ p: 6, borderRadius: 3 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" color="#1a3c6d" gutterBottom>
            Loading Structure Questions...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preparing your exam session...
          </Typography>
          {sessionId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Session ID: {sessionId}
            </Typography>
          )}
        </Paper>
      </Container>
    );
  }

  if (fetchError || questions.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, textAlign: 'center' }}>
        <Paper elevation={8} sx={{ p: 6, borderRadius: 3 }}>
          <Typography variant="h5" color="error" gutterBottom>
            ❌ Failed to Load Questions
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Unable to fetch structure questions from server.
          </Typography>
          
          {backendResponse && (
            <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, mb: 3, textAlign: 'left' }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                🛠 Debug Information:
              </Typography>
              <Typography variant="caption" component="div">
                <pre style={{ fontSize: '10px', overflow: 'auto', maxHeight: '200px' }}>
                  {JSON.stringify(backendResponse, null, 2)}
                </pre>
              </Typography>
            </Box>
          )}
          
          <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, mb: 3 }}>
            <Typography variant="body2" color="text.primary" textAlign="left">
              Possible causes:<br />
              • Data format from backend does not match<br />
              • Connection to server interrupted<br />
              • Questions not yet available in database<br />
              • Login session expired
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ bgcolor: '#1a3c6d', mr: 2, mb: 2 }}
          >
            🔄 Refresh Page
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  if (showPreCheck) {
    return (
      <PreExamCheck 
        onReady={handlePreCheckReady}
        onFail={handlePreCheckFail}
      />
    );
  }

  if (!examStarted) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, textAlign: 'center' }}>
        <Paper elevation={8} sx={{ p: 6, borderRadius: 3 }}>
          <Typography variant="h4" fontWeight="bold" color="#1a3c6d" gutterBottom>
            Structure & Written Expression
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            {questions.length} questions • {Math.floor(timeLeft / 60)} minutes
          </Typography>
          
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ Questions successfully loaded ({questions.length} questions)
          </Alert>
          
          {sessionId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              📝 Session ID: <strong>{sessionId}</strong>
            </Alert>
          )}
          
          <Box sx={{ bgcolor: '#fff3cd', p: 2, borderRadius: 2, mb: 3 }}>
            <Typography variant="body2" color="text.primary" fontWeight="bold" gutterBottom>
              ⚠ STRICT RULES
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="left">
              • Exam will run in fullscreen mode<br />
              • Do not open other tabs/applications<br />
              • Do not use keyboard shortcuts<br />
              • 3x violations = auto submit<br />
              • All questions displayed on 1 page<br />
              • <strong>Auto-save enabled</strong> - answers saved automatically
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={startExam}
            sx={{ bgcolor: '#1a3c6d', px: 6, py: 2, fontSize: 18 }}
          >
            Start Pre-Check
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <AntiCheat 
        onViolation={handleViolation}
        onTabDetected={handleTabDetection}
      />
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 3, 
            position: 'sticky', 
            top: 0, 
            zIndex: 1000,
            bgcolor: 'white',
            border: '1px solid #e0e0e0'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Box sx={{ mb: { xs: 2, sm: 0 } }}>
              <Typography variant="h5" fontWeight="bold">Structure & Written Expression</Typography>
              <Typography variant="body2" color="text.secondary">
                Answered: {answeredCount}/{questions.length} questions
              </Typography>
              {sessionId && (
                <Typography variant="caption" color="text.secondary">
                  Session: {sessionId}
                </Typography>
              )}
            </Box>
            <Box textAlign="right">
              <Typography variant="h6">
                Time: <strong style={{ color: timeLeft < 300 ? '#e74c3c' : '#1a3c6d' }}>
                  {formatTime(timeLeft)}
                </strong>
              </Typography>
              {violationCount > 0 && (
                <Typography variant="caption" color="error" display="block">
                  ⚠ Violations: {violationCount}/3
                </Typography>
              )}
              {hasOtherTabs && (
                <Typography variant="caption" sx={{ color: '#ff9800' }} display="block">
                  ⚠ Other tabs detected!
                </Typography>
              )}
              {lastSaveTime && (
                <Typography variant="caption" sx={{ color: '#2e7d32' }} display="block">
                  <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Saved: {lastSaveTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </Typography>
              )}
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                flex: 1, 
                height: 8, 
                borderRadius: 5,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: progress >= 80 ? '#2e7d32' : '#1a3c6d'
                }
              }} 
            />
            <Button
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleManualSave}
              disabled={saving || Object.keys(answers).length === 0}
              variant="outlined"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              size="small"
              color={autoSaveEnabled ? 'success' : 'default'}
              variant={autoSaveEnabled ? 'contained' : 'outlined'}
              onClick={() => setAutoSaveEnabled(!autoSaveEnabled)}
            >
              {autoSaveEnabled ? 'Auto-save ON' : 'Auto-save OFF'}
            </Button>
          </Box>
        </Paper>

        {showInstructions && (
          <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#f8f9fa', border: '2px solid #1a3c6d' }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="h6" fontWeight="bold" color="#1a3c6d" gutterBottom>
                  <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  INSTRUCTIONS - STRUCTURE & WRITTEN EXPRESSION
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  Official TOEFL Structure & Written Expression Test Instructions
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                size="small"
                onClick={hideInstructions}
                sx={{ borderColor: '#1a3c6d', color: '#1a3c6d' }}
              >
                Close
              </Button>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Card variant="outlined" sx={{ mb: 2, borderLeft: '4px solid #3b82f6' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" color="#3b82f6" gutterBottom>
                    Structure (Questions {ranges.structure ? `${ranges.structure.start}-${ranges.structure.end}` : "-"})
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                    Directions: Questions {ranges.structure ? `${ranges.structure.start}-${ranges.structure.end}` : "-"} are incomplete sentences. Choose one word or phrase that best completes the sentence.
                  </Typography>
                </CardContent>
              </Card>
              
              <Card variant="outlined" sx={{ borderLeft: '4px solid #10b981' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" color="#10b981" gutterBottom>
                    Written Expression (Questions {ranges.written ? `${ranges.written.start}-${ranges.written.end}` : "-"})
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'justify' }}>
                    Directions: In questions {ranges.written ? `${ranges.written.start}-${ranges.written.end}` : "-"}, each sentence has INCORRECT words or phrases. Identify the one word or phrase that must be changed in order for the sentence to be correct.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Alert severity="info" sx={{ mt: 3 }}>
              <strong>NOTE:</strong> For Structure questions, choose the word/phrase that BEST completes the sentence. For Written Expression, identify the ONE incorrect word/phrase in the sentence.
            </Alert>
          </Paper>
        )}

        {!showInstructions && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setShowInstructions(true)}
              startIcon={<InfoIcon />}
              sx={{ borderColor: '#1a3c6d', color: '#1a3c6d' }}
            >
              Show Instructions
            </Button>
          </Box>
        )}

        {unansweredQuestions.length > 0 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 3 }}
            icon={<ErrorOutlineIcon />}
          >
            <Typography variant="body2" fontWeight="bold">
              You have {unansweredQuestions.length} unanswered question(s)
            </Typography>
            <Typography variant="caption">
              Scroll down to find questions marked with red indicator
            </Typography>
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, userSelect: 'none' }}>
          {questions.map((q, index) => {
            const isUnanswered = unansweredQuestions.includes(index);
            
            return (
              <Box 
                key={q.id || index} 
                sx={{ 
                  mb: 4, 
                  pb: 3, 
                  borderBottom: index < questions.length - 1 ? '1px solid #e0e0e0' : 'none',
                  position: 'relative'
                }}
                ref={el => questionRefs.current[index] = el}
              >
                {q.has_answered && (
                  <Box sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: '#2e7d32',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    zIndex: 1
                  }}>
                    <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    Saved
                  </Box>
                )}
                
                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  border: isUnanswered ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  bgcolor: isUnanswered ? '#fef2f2' : (answers[index] ? '#f0f9ff' : 'transparent'),
                  transition: 'all 0.3s ease'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    color: isUnanswered ? '#dc2626' : '#1a3c6d', 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 1
                  }}>
                    {isUnanswered && <ErrorOutlineIcon sx={{ color: '#dc2626' }} />}
                    <Box sx={{ 
                      width: 30, 
                      height: 30, 
                      borderRadius: '50%', 
                      bgcolor: answers[index] ? '#4caf50' : (isUnanswered ? '#ef4444' : '#e0e0e0'),
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      color: 'white',
                      fontSize: '0.9rem'
                    }}>
                      {index + 1}
                    </Box>
                    Question {index + 1}
                    {answers[index] && (
                      <Typography 
                        component="span" 
                        variant="caption" 
                        sx={{ ml: 2, color: '#2e7d32', fontWeight: 'bold' }}
                      >
                        ✓ Answered
                      </Typography>
                    )}
                    {isUnanswered && (
                      <Typography 
                        component="span" 
                        variant="caption" 
                        sx={{ ml: 2, color: '#dc2626', fontWeight: 'bold' }}
                      >
                        ⚠ Not Answered
                      </Typography>
                    )}
                    {index < ranges.structure.end && (
                      <Typography 
                        component="span" 
                        variant="caption" 
                        sx={{ ml: 2, color: '#3b82f6', fontWeight: 'bold' }}
                      >
                        (Structure)
                      </Typography>
                    )}
                    {index >= ranges.structure.end && (
                      <Typography 
                        component="span" 
                        variant="caption" 
                        sx={{ ml: 2, color: '#10b981', fontWeight: 'bold' }}
                      >
                        (Written Expression)
                      </Typography>
                    )}
                  </Typography>
                  
                  {q.image_url && !imageErrors[q.id] && (
                    <Box sx={{ 
                      mb: 3, 
                      textAlign: 'center',
                      bgcolor: '#f5f5f5',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #e0e0e0'
                    }}>
                      <img 
                        src={q.image_url} 
                        alt={q.image_description || `Question ${index + 1}`}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '350px',
                          objectFit: 'contain',
                          display: 'block',
                          margin: '0 auto'
                        }}
                        onError={() => handleImageError(q.id)}
                      />
                      {q.image_description && (
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          display: 'block', 
                          p: 1, 
                          bgcolor: 'rgba(0,0,0,0.02)',
                          fontStyle: 'italic'
                        }}>
                          {q.image_description}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {imageErrors[q.id] && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Image cannot be loaded. Please contact supervisor if image is required.
                    </Alert>
                  )}

                  <Typography paragraph sx={{ 
                    mb: 3, 
                    fontStyle: 'italic',
                    fontSize: '1.1rem',
                    lineHeight: 1.6,
                    bgcolor: '#f9f9f9',
                    p: 2,
                    borderRadius: 2
                  }}>
                    {q.question}
                  </Typography>

                  <RadioGroup 
                    value={answers[index] || ''} 
                    onChange={(e) => handleAnswer(index, e.target.value)}
                    sx={{ ml: 1 }}
                  >
                    {q.options.map((opt, i) => (
                      <FormControlLabel 
                        key={opt.value || i}
                        value={opt.value} 
                        control={<Radio color="primary" />} 
                        label={
                          <Typography sx={{ 
                            fontSize: '1rem',
                            color: answers[index] === opt.value ? '#1a3c6d' : 'inherit',
                            fontWeight: answers[index] === opt.value ? 'bold' : 'normal'
                          }}>
                            <strong>{opt.value}.</strong> {opt.text}
                          </Typography>
                        }
                        sx={{ 
                          mb: 1.5, 
                          cursor: 'pointer !important',
                          '&:hover': {
                            bgcolor: '#f0f7ff',
                            borderRadius: 2
                          },
                          p: 1.5,
                          border: answers[index] === opt.value ? '1px solid #1a3c6d' : '1px solid transparent',
                          borderRadius: 2,
                          transition: 'all 0.2s'
                        }} 
                      />
                    ))}
                  </RadioGroup>
                </Box>
              </Box>
            );
          })}

          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={handleFinish}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
              sx={{ 
                bgcolor: '#1a3c6d', 
                px: 8, 
                py: 2,
                fontSize: 18,
                cursor: 'pointer !important',
                '&:hover': {
                  bgcolor: '#0d2b5c',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s',
                boxShadow: 3,
                '&:disabled': {
                  bgcolor: '#9ca3af'
                }
              }}
            >
              {saving ? 'Saving...' : 'Continue to Reading →'}
            </Button>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              All answers are automatically saved. Last save: {lastSaveTime ? lastSaveTime.toLocaleTimeString() : 'Never'}
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* Save Status Snackbar */}
      <Snackbar
        open={saveStatus.show}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={saveStatus.type} sx={{ width: '100%' }}>
          {saveStatus.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Structure;
